/* =============================================================================
 * MessageBubble + DayDivider — APS chat primitives (Track D)
 * -----------------------------------------------------------------------------
 * Presentation-only conversation primitives, reused across the Gmail thread
 * view, the X-DM thread view, and any future in-app messaging. Tokenized for
 * web↔mobile parity.
 *
 * Aesthetic (on-brand, not the loud CTA orange):
 *   - outgoing ('out')  → navy surface (ink→ink2 gradient), white text, anchored right
 *   - incoming ('in')   → neutral fill, ink text, anchored left
 * The corner nearest the anchoring edge is notched (radius.notch) so a run of
 * bubbles reads as one grouped conversation, iMessage-tight.
 * ========================================================================== */
import React from 'react';
import { Check } from 'react-feather';
import { radius, font, space } from '../../styles/tokens';

export type MessageDirection = 'in' | 'out';
export type MessageStatus = 'sending' | 'sent' | 'read';

export interface MessageBubbleProps {
  direction: MessageDirection;
  children: React.ReactNode;
  /** Short timestamp, e.g. "2:14 PM". Rendered under the bubble, on its side. */
  timestamp?: string;
  /** Sender label — shown above the bubble (use for the first bubble in a group). */
  author?: string;
  /** Delivery state for outgoing bubbles only. */
  status?: MessageStatus;
  /** True for the last bubble in a same-sender run — draws the anchoring notch. */
  tail?: boolean;
  style?: React.CSSProperties;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  direction, children, timestamp, author, status, tail = true, style,
}) => {
  const isOut = direction === 'out';

  // The corner nearest the anchoring edge tightens on the tail bubble.
  const notched = tail ? radius.notch : radius.bubble;
  const bubbleRadius = isOut
    ? `${radius.bubble}px ${radius.bubble}px ${notched}px ${radius.bubble}px`
    : `${radius.bubble}px ${radius.bubble}px ${radius.bubble}px ${notched}px`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isOut ? 'flex-end' : 'flex-start',
        maxWidth: '78%',
        alignSelf: isOut ? 'flex-end' : 'flex-start',
        ...style,
      }}
    >
      {author && (
        <span
          style={{
            fontSize: 11,
            fontWeight: font.weight.semibold as any,
            color: 'var(--aps-text-subtle)',
            margin: `0 ${space[1]}px ${space[1]}px`,
            fontFamily: font.sans,
          }}
        >
          {author}
        </span>
      )}

      <div
        style={{
          padding: '10px 14px',
          borderRadius: bubbleRadius,
          fontSize: 14,
          lineHeight: 1.45,
          fontFamily: font.sans,
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          background: isOut
            ? 'linear-gradient(135deg, var(--aps-ink) 0%, var(--aps-ink-2) 100%)'
            : 'var(--aps-fill)',
          color: isOut ? 'var(--aps-on-ink)' : 'var(--aps-ink)',
          border: isOut ? '1px solid transparent' : '1px solid var(--aps-border)',
        }}
      >
        {children}
      </div>

      {(timestamp || (isOut && status)) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            margin: `${space[1]}px ${space[1]}px 0`,
            fontSize: 11,
            color: 'var(--aps-text-subtle)',
            fontFamily: font.sans,
          }}
        >
          {timestamp && <span>{timestamp}</span>}
          {isOut && status === 'read' && (
            <Check size={12} aria-hidden style={{ color: 'var(--aps-success)' }} />
          )}
          {isOut && status === 'sent' && <Check size={12} aria-hidden />}
          {isOut && status === 'sending' && <span aria-hidden>·</span>}
        </div>
      )}
    </div>
  );
};

export interface DayDividerProps {
  label: string;
}

/** Centered date separator between message groups (e.g. "Today", "Aug 18"). */
export const DayDivider: React.FC<DayDividerProps> = ({ label }) => (
  <div
    role="separator"
    aria-label={label}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: space[3],
      margin: `${space[4]}px 0`,
      color: 'var(--aps-text-subtle)',
      fontFamily: font.sans,
    }}
  >
    <span style={{ flex: 1, height: 1, background: 'var(--aps-border)' }} />
    <span style={{ fontSize: 11, fontWeight: font.weight.semibold as any, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
      {label}
    </span>
    <span style={{ flex: 1, height: 1, background: 'var(--aps-border)' }} />
  </div>
);

export default MessageBubble;
